import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EFFORT_BUCKETS, EFFORT_META, ESTIMATE_CHOICES,
  bucketFor, groupByEffort, formatMinutes
} from './choreEffort';
import './RoomBoard.css';

const UNASSIGNED_ROOM = '__none__';

/**
 * Room-by-room view of outstanding work.
 *
 * Deliberately two screens rather than one list:
 *  1. Pick a room — narrows the whole house down to one place.
 *  2. Inside a room, tasks are grouped by how long they take, quick wins first, so a
 *     spare two minutes has an obvious answer instead of requiring a decision.
 *
 * A room still isn't a unit of work you complete in one go — that's the trap of "clean the
 * kitchen". It's a unit of *attention*. The list exists so nothing has to be held in memory.
 */
function RoomBoard({
  rooms, chores, roomSummaries, loading,
  onQuickCapture, onSetEstimate, onComplete, onEdit, onSelectRoomForFocus
}) {
  const { t, i18n } = useTranslation();
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [captureText, setCaptureText] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const captureRef = useRef(null);

  const roomName = (room) => (i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn;

  const selectedRoom = rooms.find(r => r.id === selectedRoomId) || null;
  const viewingUnassigned = selectedRoomId === UNASSIGNED_ROOM;

  const roomChores = useMemo(() => {
    if (!selectedRoomId) return [];
    const open = chores.filter(c => c.status !== 2);
    return viewingUnassigned
      ? open.filter(c => !c.roomId)
      : open.filter(c => c.roomId === selectedRoomId);
  }, [chores, selectedRoomId, viewingUnassigned]);

  const grouped = useMemo(() => groupByEffort(roomChores), [roomChores]);

  const unassignedCount = useMemo(
    () => chores.filter(c => c.status !== 2 && !c.roomId).length,
    [chores]
  );

  // Summaries come from the server, but a chore captured seconds ago should appear
  // immediately rather than after the next summary refresh.
  const liveSummaryFor = (roomId) => {
    const open = chores.filter(c => c.status !== 2 && c.roomId === roomId);
    return {
      openCount: open.length,
      quickWinCount: open.filter(c => bucketFor(c.estimatedMinutes) === 'quickWin').length,
      unestimatedCount: open.filter(c => c.estimatedMinutes === null || c.estimatedMinutes === undefined).length
    };
  };

  useEffect(() => {
    if (selectedRoomId && captureRef.current) captureRef.current.focus();
  }, [selectedRoomId]);

  const handleCapture = async (e) => {
    e.preventDefault();
    const titles = captureText
      .split('\n')
      .map(line => line.replace(/^[-*\u2022]\s*/, '').trim())
      .filter(Boolean);
    if (titles.length === 0) return;

    setCapturing(true);
    try {
      await onQuickCapture({
        titles,
        roomId: viewingUnassigned ? null : selectedRoomId
      });
      setCaptureText('');
      captureRef.current?.focus();
    } finally {
      setCapturing(false);
    }
  };

  const toggleCollapsed = (bucket) =>
    setCollapsed(prev => ({ ...prev, [bucket]: !prev[bucket] }));

  if (loading && rooms.length === 0) {
    return <div className="chore-loading">{t('chores.rooms.loading')}</div>;
  }

  // ---- Room picker ----------------------------------------------------------
  if (!selectedRoomId) {
    return (
      <div className="room-board">
        <p className="room-board-intro">{t('chores.rooms.pickIntro')}</p>

        <div className="room-grid">
          {rooms.map(room => {
            const summary = liveSummaryFor(room.id);
            const serverSummary = roomSummaries.find(s => (s.room?.id || s.roomId) === room.id);
            const total = summary.openCount;
            return (
              <button
                key={room.id}
                className={`room-tile ${total === 0 ? 'room-tile-clear' : ''}`}
                onClick={() => setSelectedRoomId(room.id)}
              >
                <span className="room-tile-icon" aria-hidden="true">{room.icon || '🏠'}</span>
                <span className="room-tile-name">{roomName(room)}</span>
                {total === 0 ? (
                  <span className="room-tile-clear-label">{t('chores.rooms.allClear')}</span>
                ) : (
                  <span className="room-tile-counts">
                    <span className="room-tile-open">{t('chores.rooms.openCount', { count: total })}</span>
                    {summary.quickWinCount > 0 && (
                      <span className="room-tile-quick">
                        ⚡ {t('chores.rooms.quickCount', { count: summary.quickWinCount })}
                      </span>
                    )}
                    {summary.unestimatedCount > 0 && (
                      <span className="room-tile-unestimated">
                        ❓ {summary.unestimatedCount}
                      </span>
                    )}
                  </span>
                )}
                {serverSummary?.estimatedMinutesTotal > 0 && (
                  <span className="room-tile-time">
                    ~{formatMinutes(serverSummary.estimatedMinutesTotal, t)}
                  </span>
                )}
              </button>
            );
          })}

          {unassignedCount > 0 && (
            <button
              className="room-tile room-tile-unassigned"
              onClick={() => setSelectedRoomId(UNASSIGNED_ROOM)}
            >
              <span className="room-tile-icon" aria-hidden="true">📭</span>
              <span className="room-tile-name">{t('chores.rooms.noRoom')}</span>
              <span className="room-tile-counts">
                <span className="room-tile-open">{t('chores.rooms.openCount', { count: unassignedCount })}</span>
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---- Single room ----------------------------------------------------------
  const heading = viewingUnassigned
    ? `📭 ${t('chores.rooms.noRoom')}`
    : `${selectedRoom?.icon || '🏠'} ${selectedRoom ? roomName(selectedRoom) : ''}`;

  const quickWins = grouped.quickWin.length;
  const unestimated = grouped.unknown.length;

  return (
    <div className="room-board">
      <div className="room-detail-header">
        <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={() => setSelectedRoomId(null)}>
          ← {t('chores.rooms.allRooms')}
        </button>
        <h3 className="room-detail-title">{heading}</h3>
        <span className="room-detail-count">
          {t('chores.rooms.openCount', { count: roomChores.length })}
        </span>
        {quickWins > 0 && onSelectRoomForFocus && (
          <button
            className="chore-btn chore-btn-primary chore-btn-small"
            onClick={() => onSelectRoomForFocus(viewingUnassigned ? null : selectedRoomId)}
          >
            ⚡ {t('chores.rooms.startQuickWins', { count: quickWins })}
          </button>
        )}
      </div>

      <form className="room-capture" onSubmit={handleCapture}>
        <label className="room-capture-label" htmlFor="room-capture-input">
          {t('chores.rooms.captureLabel')}
        </label>
        <textarea
          id="room-capture-input"
          ref={captureRef}
          className="room-capture-input"
          value={captureText}
          onChange={(e) => setCaptureText(e.target.value)}
          onKeyDown={(e) => {
            // Enter submits so a stream of thought can be typed without reaching for
            // the mouse; Shift+Enter still adds a line for multi-item dumps.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleCapture(e);
            }
          }}
          placeholder={t('chores.rooms.capturePlaceholder')}
          rows={2}
        />
        <div className="room-capture-actions">
          <span className="room-capture-hint">{t('chores.rooms.captureHint')}</span>
          <button
            type="submit"
            className="chore-btn chore-btn-primary chore-btn-small"
            disabled={capturing || !captureText.trim()}
          >
            {capturing ? t('chores.rooms.capturing') : t('chores.rooms.captureAdd')}
          </button>
        </div>
      </form>

      {roomChores.length === 0 && (
        <div className="room-empty">
          <span className="room-empty-icon" aria-hidden="true">✨</span>
          <p>{t('chores.rooms.emptyRoom')}</p>
        </div>
      )}

      {unestimated > 0 && (
        <p className="room-triage-nudge">
          {t('chores.rooms.triageNudge', { count: unestimated })}
        </p>
      )}

      {EFFORT_BUCKETS.map(bucket => {
        const items = grouped[bucket];
        if (items.length === 0) return null;
        const isCollapsed = collapsed[bucket];

        return (
          <section key={bucket} className={`effort-group ${EFFORT_META[bucket].className}`}>
            <button
              className="effort-group-header"
              onClick={() => toggleCollapsed(bucket)}
              aria-expanded={!isCollapsed}
            >
              <span className="effort-group-icon" aria-hidden="true">{EFFORT_META[bucket].icon}</span>
              <span className="effort-group-title">{t(`chores.effort.${bucket}`)}</span>
              <span className="effort-group-range">{t(`chores.effort.${bucket}Range`)}</span>
              <span className="effort-group-count">{items.length}</span>
              <span className="effort-group-chevron" aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
            </button>

            {!isCollapsed && (
              <ul className="effort-list">
                {items.map(chore => (
                  <li key={chore.id} className="effort-item">
                    <button
                      className="effort-item-check"
                      onClick={() => onComplete(chore.id)}
                      title={t('chores.card.complete')}
                      aria-label={`${t('chores.card.complete')}: ${chore.title}`}
                    >
                      ○
                    </button>

                    <span className="effort-item-title">{chore.title}</span>

                    {chore.estimatedMinutes != null && (
                      <span className="effort-item-minutes">
                        {formatMinutes(chore.estimatedMinutes, t)}
                      </span>
                    )}

                    <EstimatePicker
                      chore={chore}
                      onSetEstimate={onSetEstimate}
                      t={t}
                    />

                    {onEdit && (
                      <button
                        className="effort-item-edit"
                        onClick={() => onEdit(chore)}
                        aria-label={`${t('chores.card.edit')}: ${chore.title}`}
                      >
                        ✏️
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/**
 * One-tap estimating. Triage is a separate, low-effort pass from capture — being asked
 * "how long?" while trying to write things down is what stops the list getting written.
 */
function EstimatePicker({ chore, onSetEstimate, t }) {
  const [open, setOpen] = useState(false);
  const hasEstimate = chore.estimatedMinutes != null;

  return (
    <span className="estimate-picker-wrapper">
      <button
        className={`estimate-picker-toggle ${hasEstimate ? '' : 'estimate-missing'}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={t('chores.effort.setEstimate')}
        title={t('chores.effort.setEstimate')}
      >
        {hasEstimate ? '⏱' : `⏱ ${t('chores.effort.howLong')}`}
      </button>
      {open && (
        <span className="estimate-picker">
          {ESTIMATE_CHOICES.map(minutes => (
            <button
              key={minutes}
              className={`estimate-picker-item ${chore.estimatedMinutes === minutes ? 'active' : ''}`}
              onClick={() => { onSetEstimate(chore.id, minutes); setOpen(false); }}
            >
              {formatMinutes(minutes, t)}
            </button>
          ))}
          {hasEstimate && (
            <button
              className="estimate-picker-item estimate-picker-clear"
              onClick={() => { onSetEstimate(chore.id, null); setOpen(false); }}
            >
              {t('chores.effort.clearEstimate')}
            </button>
          )}
        </span>
      )}
    </span>
  );
}

export default RoomBoard;
